/**
 * EmailScoutScanner - Client-Side Email Scanning Component
 *
 * This is the core UI for the "White-Glove Onboarding" client-side scout.
 * Implements the Dan Martell "activation" flow:
 * 1. User clicks "Connect Google Workspace" → GIS consent overlay
 * 2. Browser scans email → shows progress
 * 3. User sees preview → confirms import
 * 4. Files upload directly to Valutten
 *
 * Uses Google Identity Services (GIS) token model:
 * - No redirect URIs needed (token returned via JS callback)
 * - Only requires Authorized JavaScript Origins on the OAuth client
 * - Short-lived token (1 hour, no refresh token stored)
 * - User sees exactly what's being scanned
 *
 * POPUP SAFETY: The GIS requestAccessToken() call opens a popup.
 * Chrome only allows popups from synchronous user-gesture handlers.
 * The GIS script is pre-loaded on mount so handleConnectGmail can call
 * requestAccessToken() synchronously from the click — no async gap.
 * The button is disabled until GIS is ready, guaranteeing this invariant.
 *
 * PARALLEL UPLOAD ENGINE: Files are uploaded in batches of 10 concurrent
 * to fit 600+ files within the 1-hour OAuth token window.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  scanGmailForCommissions,
  downloadAttachment,
  calculateFileHash,
  ScoutedFile,
  ScanProgress,
} from '../../utils/gmailScout';
import { httpsCallable } from 'firebase/functions';
import { getDocs, query as fsQuery, collection, where } from 'firebase/firestore';
import { functions, db } from '../../firebase';
import { Loader2 } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import { LoanBookWizard, LoanBookAssignment } from '../onboarding/LoanBookWizard';

// Google Identity Services (GIS) configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || '';
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/gmail.readonly';

/** Max concurrent uploads per batch — safe for Gmail API + Firebase callables */
const UPLOAD_BATCH_SIZE = 10;

// GIS type declarations
interface GisTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  error?: string;
  error_description?: string;
  error_uri?: string;
}

interface GisTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string; hint?: string }) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GisTokenResponse) => void;
            error_callback?: (error: { type: string; message: string }) => void;
          }) => GisTokenClient;
        };
      };
    };
  }
}

/**
 * Load Google Identity Services script if not already loaded.
 * Cached at module level — multiple callers share the same load attempt.
 */
let gisLoadPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;

  gisLoadPromise = new Promise<void>((resolve, reject) => {
    // Check if script tag already exists (loading in progress)
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google?.accounts?.oauth2) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkLoaded);
        reject(new Error('Google Identity Services script timed out'));
      }, 10000);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
  return gisLoadPromise;
}

interface EmailScoutScannerProps {
  companyId: string;
  officeId?: string; // Optional - defaults to 'default'
  templateId?: string; // Optional - auto-detected from aggregator
  customSenderEmails?: string[]; // Additional sender emails from user input
  stagingMode?: boolean; // When true, use issueOnboardingStagingUrl (no template required)
  onComplete?: (results: ImportResults) => void;
  onError?: (error: Error) => void;
}

interface ImportResults {
  filesFound: number;
  filesImported: number;
  filesDuplicate: number;
  filesErrored: number;
  errors: string[];
}

interface SingleUploadResult {
  success: boolean;
  isDuplicate?: boolean;
  error?: string;
}

type Phase =
  | 'idle'
  | 'authenticating'
  | 'scanning'
  | 'preview'
  | 'importing'
  | 'complete'
  | 'error';

export const EmailScoutScanner: React.FC<EmailScoutScannerProps> = ({
  companyId,
  officeId = 'default', // Will be resolved during upload
  templateId = 'auto', // Auto-detected from aggregator code
  customSenderEmails = [],
  stagingMode = false, // Use staging endpoint when no template available
  onComplete,
  onError,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenIssuedAt, setTokenIssuedAt] = useState<number | null>(null);
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
  const [scoutedFiles, setScoutedFiles] = useState<ScoutedFile[]>([]);
  const [dedupCount, setDedupCount] = useState(0);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<ImportResults | null>(null);
  const [failedAssignments, setFailedAssignments] = useState<LoanBookAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const tokenClientRef = useRef<GisTokenClient | null>(null);
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanStartTimeRef = useRef<number>(0);
  const [gisReady, setGisReady] = useState(false);
  const [gisLoadFailed, setGisLoadFailed] = useState(false);
  const [priorGroupNames, setPriorGroupNames] = useState<Map<string, { name: string; officeId: string; officeName: string }>>(new Map());
  const [scanYearFrom, setScanYearFrom] = useState(new Date().getFullYear() - 10);
  const [scanYearTo, setScanYearTo] = useState(new Date().getFullYear());

  // Stable references for callables (avoid recreating per file)
  const getStagingUrl = useRef(httpsCallable<any, any>(functions, 'issueOnboardingStagingUrl'));
  const getPipelineUrl = useRef(httpsCallable<any, any>(functions, 'getSignedUploadUrl'));
  const confirmPipelineUpload = useRef(httpsCallable<any, any>(functions, 'confirmScoutUpload'));
  const confirmStaging = useRef(httpsCallable<any, any>(functions, 'confirmStagingUpload'));

  // Pre-load Google Identity Services script on mount.
  // This ensures requestAccessToken() can be called synchronously from user click.
  // (async gap between click and popup open causes Chrome to block the popup)
  useEffect(() => {
    let mounted = true;
    loadGisScript()
      .then(() => { if (mounted) setGisReady(true); })
      .catch((err) => {
        if (mounted) {
          console.warn('[EmailScoutScanner] Failed to pre-load GIS:', err);
          setGisLoadFailed(true);
        }
      });
    return () => {
      mounted = false;
      if (authTimeoutRef.current) { clearTimeout(authTimeoutRef.current); authTimeoutRef.current = null; }
    };
  }, []);

  /**
   * Upload a single file (staging or pipeline path).
   * Isolated for use with Promise.allSettled in batch engine.
   */
  const uploadSingleFile = useCallback(async (
    assignment: LoanBookAssignment,
  ): Promise<SingleUploadResult> => {
    const { file, loanBookGroupKey, subscriberLoanBookName, subscriberOfficeId, subscriberOfficeName } = assignment;

    const content = await downloadAttachment(file.messageId, file.attachmentId);
    const fileHash = await calculateFileHash(content);

    const useStaging = stagingMode || !file.aggregatorCode;

    if (useStaging) {
      const urlResponse = await getStagingUrl.current({
        companyId,
        officeId: subscriberOfficeId || officeId,
        fileName: file.filename,
        originalFileName: file.filename,
        fileSize: file.size,
        contentType: file.mimeType,
        entityCode: file.entityCode || null,
        subscriberLoanBookName,
        subscriberOfficeId,
        subscriberOfficeName,
        loanBookGroupKey,
        emailMessageId: file.messageId || null,
      });

      const { signedUrl, uploadId } = urlResponse.data;
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.mimeType },
        body: content,
      });

      if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.statusText}`);

      // Confirm the upload so dedup can distinguish real uploads from ghost intents
      await confirmStaging.current({ uploadId, companyId });

      return { success: true };
    } else {
      const effectiveTemplateId = templateId === 'auto' && file.aggregatorCode
        ? file.aggregatorCode.toLowerCase()
        : templateId;

      const urlResponse = await getPipelineUrl.current({
        companyId,
        officeId: subscriberOfficeId || officeId,
        templateId: effectiveTemplateId,
        filename: file.filename,
        mimeType: file.mimeType,
        fileSize: file.size,
        fileHash,
        emailMetadata: {
          messageId: file.messageId,
          senderEmail: file.senderEmail,
          emailDate: file.emailDate.toISOString(),
          subject: file.emailSubject,
        },
        aggregatorCode: file.aggregatorCode,
        entityCode: file.entityCode || null,
        subscriberLoanBookName,
        runNumber: file.runNumber,
        periodMonth: file.periodMonth,
      });

      if (urlResponse.data.isDuplicate) {
        return { success: false, isDuplicate: true };
      }

      if (!urlResponse.data.success || !urlResponse.data.signedUrl) {
        throw new Error(urlResponse.data.error || 'Failed to get upload URL');
      }

      const uploadHeaders: Record<string, string> = {
        'Content-Type': file.mimeType,
        ...(urlResponse.data.requiredHeaders || {}),
      };
      const uploadResponse = await fetch(urlResponse.data.signedUrl, {
        method: 'PUT',
        headers: uploadHeaders,
        body: content,
      });

      if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.statusText}`);

      await confirmPipelineUpload.current({
        uploadId: urlResponse.data.uploadId,
        companyId,
      });

      return { success: true };
    }
  }, [companyId, officeId, templateId, stagingMode]);

  // Start scanning after authentication
  const handleStartScan = useCallback(async (token: string) => {
    setPhase('scanning');
    scanStartTimeRef.current = Date.now();
    setScanProgress({
      phase: 'searching',
      messagesFound: 0,
      messagesScanned: 0,
      filesFound: 0,
      filesMatched: 0,
    });

    try {
      // Merge custom sender emails with known senders (deduped)
      const senderPatterns = customSenderEmails.length > 0
        ? [...new Set([...customSenderEmails.map(e => e.trim().toLowerCase()).filter(Boolean)])]
        : undefined; // undefined = use defaults in scanGmailForCommissions

      const hasCustomSenders = customSenderEmails.length > 0;
      // Convert year range to Date objects for Gmail query filtering
      const afterDate = new Date(scanYearFrom, 0, 1); // Jan 1 of start year
      const beforeDate = new Date(scanYearTo + 1, 0, 1); // Jan 1 of year after end year (exclusive)
      const files = await scanGmailForCommissions(token, {
        senderPatterns,
        afterDate,
        beforeDate,
        onProgress: setScanProgress,
        stagingMode,
        hasCustomSenders,
      });

      // Dedup: exclude files already confirmed-uploaded for this company
      // Uses compound key (filename + emailMessageId) to prevent false-positive dedup
      // when lenders reuse the same filename across different months
      let newFiles = files;
      let skipped = 0;
      try {
        const snap = await getDocs(
          fsQuery(collection(db, 'upload_intents'), where('companyId', '==', companyId))
        );
        const uploadedKeys = new Set<string>();
        // Build map of prior group names from confirmed intents
        // Key = entityCode or loanBookGroupKey, Value = {name, officeId, officeName}
        const priorNames = new Map<string, { name: string; officeId: string; officeName: string }>();
        snap.docs.forEach(doc => {
          const d = doc.data();
          // Only count confirmed uploads — ghost intents (no uploadConfirmedAt) are ignored
          if (d.uploadConfirmedAt) {
            const key = d.emailMessageId
              ? `${d.originalFileName}::${d.emailMessageId}`
              : d.originalFileName; // Fallback for inline uploads (no messageId)
            if (key) uploadedKeys.add(key);

            // Extract prior loan book names and office assignments for the wizard
            const groupKey = d.entityCode || d.loanBookGroupKey;
            if (groupKey && d.subscriberLoanBookName && d.subscriberLoanBookName !== groupKey) {
              // Only store if we haven't seen this group key yet (first occurrence wins)
              if (!priorNames.has(groupKey)) {
                priorNames.set(groupKey, {
                  name: d.subscriberLoanBookName,
                  officeId: d.subscriberOfficeId || '',
                  officeName: d.subscriberOfficeName || '',
                });
              }
            }
          }
        });
        setPriorGroupNames(priorNames);
        if (uploadedKeys.size > 0) {
          newFiles = files.filter(f => {
            const compoundKey = f.messageId
              ? `${f.filename}::${f.messageId}`
              : f.filename;
            // Check compound key first (new intents), then plain filename
            // fallback (legacy intents created before emailMessageId was added)
            return !uploadedKeys.has(compoundKey) && !uploadedKeys.has(f.filename);
          });
          skipped = files.length - newFiles.length;
        }
      } catch (dedupErr) {
        // Dedup query failure is non-fatal — show all files
        console.warn('[EmailScoutScanner] Dedup query failed, showing all files:', dedupErr);
      }

      setDedupCount(skipped);
      setScoutedFiles(newFiles);
      setPhase('preview');
    } catch (err) {
      console.error('[EmailScoutScanner] Scan error:', err);
      setError((err as Error).message);
      setPhase('error');
      onError?.(err as Error);
    }
  }, [onError, customSenderEmails, companyId, stagingMode, scanYearFrom, scanYearTo]);

  // Start OAuth flow using Google Identity Services (GIS) token model.
  // SYNCHRONOUS handler — no await before requestAccessToken().
  // Button is disabled when !gisReady, so window.google is guaranteed available.
  const handleConnectGmail = useCallback(() => {
    setPhase('authenticating');
    setError(null);

    try {
      // Create fresh token client to capture latest callback closures
      tokenClientRef.current = window.google!.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_SCOPES,
        callback: (response: GisTokenResponse) => {
          if (authTimeoutRef.current) { clearTimeout(authTimeoutRef.current); authTimeoutRef.current = null; }
          if (response.error) {
            setError(response.error_description || response.error);
            setPhase('error');
            onError?.(new Error(response.error_description || response.error));
            return;
          }
          setAccessToken(response.access_token);
          setTokenIssuedAt(Date.now());
          handleStartScan(response.access_token);
        },
        error_callback: (err: { type: string; message: string }) => {
          if (authTimeoutRef.current) { clearTimeout(authTimeoutRef.current); authTimeoutRef.current = null; }
          // User closed consent overlay or popup was blocked
          if (err.type === 'popup_closed') {
            setPhase('idle');
            return;
          }
          setError(err.message || 'Authentication failed');
          setPhase('error');
          onError?.(new Error(err.message || 'Authentication failed'));
        },
      });

      // Request access token — opens Google consent popup synchronously
      tokenClientRef.current.requestAccessToken();

      // Safety timeout: if neither callback fires within 20s, show guidance.
      // This catches popup-blocked, popup-hidden-behind-window, and GIS silent failures.
      authTimeoutRef.current = setTimeout(() => {
        // Only fire if still stuck in authenticating (use functional setState to avoid stale closure)
        setPhase((current) => {
          if (current === 'authenticating') {
            setError(
              'The Google sign-in popup may not have appeared. Please check for a blocked popup icon in your address bar, or look for the popup behind this window.'
            );
            return 'error';
          }
          return current;
        });
      }, 20000);
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
      onError?.(err as Error);
    }
  }, [onError, handleStartScan]);

  // Old handleImport, handleToggleFile, handleSelectAll removed — replaced by LoanBookWizard

  // Reset to start over
  const handleReset = useCallback(() => {
    if (authTimeoutRef.current) { clearTimeout(authTimeoutRef.current); authTimeoutRef.current = null; }
    setPhase('idle');
    setAccessToken(null);
    setTokenIssuedAt(null);
    setScanProgress(null);
    setScoutedFiles([]);
    setDedupCount(0);
    setImportProgress({ current: 0, total: 0 });
    setResults(null);
    setFailedAssignments([]);
    setError(null);
  }, []);

  // Handle import from the LoanBookWizard — parallel batch engine
  const handleWizardImport = useCallback(async (assignments: LoanBookAssignment[], selectedFileKeys: Set<string>) => {
    if (!accessToken) return;

    // Token TTL guard — GIS tokens expire after 1 hour
    if (tokenIssuedAt && Date.now() - tokenIssuedAt > 55 * 60 * 1000) {
      setError('Your Google session expired (1-hour limit). Please reconnect to retry.');
      setPhase('error');
      return;
    }

    setPhase('importing');
    setImportProgress({ current: 0, total: assignments.length });

    const importResults: ImportResults = {
      filesFound: assignments.length,
      filesImported: 0,
      filesDuplicate: 0,
      filesErrored: 0,
      errors: [],
    };

    const failed: LoanBookAssignment[] = [];

    // Process in batches of UPLOAD_BATCH_SIZE concurrent uploads
    for (let batchStart = 0; batchStart < assignments.length; batchStart += UPLOAD_BATCH_SIZE) {
      // Check token TTL before each batch — catch expiry mid-upload
      if (tokenIssuedAt && Date.now() - tokenIssuedAt > 55 * 60 * 1000) {
        const remaining = assignments.slice(batchStart);
        failed.push(...remaining);
        importResults.filesErrored += remaining.length;
        importResults.errors.push(
          `Google session expired after ${importResults.filesImported} files. Click "Retry" to reconnect and upload the remaining ${remaining.length} files.`
        );
        break;
      }

      const batch = assignments.slice(batchStart, batchStart + UPLOAD_BATCH_SIZE);

      const batchResults = await Promise.allSettled(
        batch.map(assignment => uploadSingleFile(assignment))
      );

      batchResults.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value.success) {
          importResults.filesImported++;
        } else if (result.status === 'fulfilled' && result.value.isDuplicate) {
          importResults.filesDuplicate++;
        } else {
          importResults.filesErrored++;
          const errMsg = result.status === 'rejected'
            ? (result.reason as Error)?.message || 'Unknown error'
            : result.value.error || 'Upload failed';
          importResults.errors.push(`${batch[idx].file.filename}: ${errMsg}`);
          failed.push(batch[idx]);
        }
      });

      setImportProgress({
        current: Math.min(batchStart + UPLOAD_BATCH_SIZE, assignments.length),
        total: assignments.length,
      });

      // Circuit breaker: if 3+ auth errors in a batch, token is dead — stop wasting time
      const batchAuthErrors = batchResults.filter(r =>
        r.status === 'rejected' &&
        /401|403|auth|token|credential|expired/i.test((r.reason as Error)?.message || '')
      ).length;
      if (batchAuthErrors >= 3) {
        const remaining = assignments.slice(batchStart + UPLOAD_BATCH_SIZE);
        failed.push(...remaining);
        importResults.filesErrored += remaining.length;
        importResults.errors.push('Google session expired — reconnect to retry remaining files');
        break;
      }
    }

    setFailedAssignments(failed);
    setResults(importResults);
    setPhase('complete');
    onComplete?.(importResults);
  }, [accessToken, tokenIssuedAt, uploadSingleFile, onComplete]);

  // Retry GIS load (for ad-blocker recovery)
  const handleRetryGisLoad = useCallback(() => {
    setGisLoadFailed(false);
    gisLoadPromise = null; // allow fresh attempt
    loadGisScript()
      .then(() => setGisReady(true))
      .catch(() => setGisLoadFailed(true));
  }, []);

  return (
    <div className="rounded-lg">
      {/* Idle State - Connect Button */}
      {phase === 'idle' && (
        <div className="text-center py-6">
          {gisLoadFailed ? (
            <>
              <p className="text-yellow-400 text-sm mb-3">
                Google sign-in could not load. This may be caused by an ad blocker or network restriction.
              </p>
              <button
                onClick={handleRetryGisLoad}
                className="text-sm text-blue-400 hover:text-blue-300 underline"
              >
                Retry loading
              </button>
            </>
          ) : (
            <>
              {/* Year range picker */}
              <div className="mb-5 flex items-center justify-center gap-2 text-sm text-gray-300">
                <span>Scan emails from</span>
                <select
                  value={scanYearFrom}
                  onChange={(e) => {
                    const year = Number(e.target.value);
                    setScanYearFrom(year);
                    if (year > scanYearTo) setScanYearTo(year);
                  }}
                  className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {Array.from({ length: new Date().getFullYear() - 2010 + 1 }, (_, i) => 2010 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <span>to</span>
                <select
                  value={scanYearTo}
                  onChange={(e) => {
                    const year = Number(e.target.value);
                    setScanYearTo(year);
                    if (year < scanYearFrom) setScanYearFrom(year);
                  }}
                  className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {Array.from({ length: new Date().getFullYear() - 2010 + 1 }, (_, i) => 2010 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleConnectGmail}
                disabled={!gisReady}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!gisReady ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Loading Google Sign-in...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Connect Google Workspace
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-gray-500">
                Works with Gmail and Google Workspace business accounts.
              </p>
            </>
          )}
        </div>
      )}

      {/* Authenticating State */}
      {phase === 'authenticating' && (
        <div className="text-center py-8">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-300">Connecting to Google...</p>
          <p className="text-sm text-gray-500 mt-2">
            Please complete the authorization in the popup window.
          </p>
          <p className="text-xs text-gray-600 mt-4">
            Don't see a popup? Check for a blocked popup icon in your browser's address bar,
            or look for the window behind this page.
          </p>
          <button
            onClick={handleReset}
            className="mt-4 text-sm text-blue-400 hover:text-blue-300 underline"
          >
            Cancel and try again
          </button>
        </div>
      )}

      {/* Scanning State */}
      {phase === 'scanning' && scanProgress && (
        <div className="py-8">
          <div className="text-center mb-6">
            <Spinner size="lg" className="mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white">
              {scanProgress.phase === 'searching' && 'Searching for commission emails...'}
              {scanProgress.phase === 'listing' && 'Listing messages...'}
              {scanProgress.phase === 'analyzing' && 'Analyzing attachments...'}
            </h3>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 max-w-md mx-auto">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Messages found:</span>
                <span className="ml-2 font-medium text-gray-200">{scanProgress.messagesFound}</span>
              </div>
              <div>
                <span className="text-gray-400">Scanned:</span>
                <span className="ml-2 font-medium text-gray-200">{scanProgress.messagesScanned}</span>
              </div>
              <div>
                <span className="text-gray-400">Attachments:</span>
                <span className="ml-2 font-medium text-gray-200">{scanProgress.filesFound}</span>
              </div>
              <div>
                <span className="text-gray-400">Matched:</span>
                <span className="ml-2 font-medium text-green-400">
                  {scanProgress.filesMatched}
                </span>
              </div>
            </div>
            {scanProgress.currentMessage && (
              <p className="mt-3 text-xs text-gray-500 truncate">
                Current: {scanProgress.currentMessage}
              </p>
            )}
            {scanProgress.messagesScanned > 0 && scanProgress.messagesFound > scanProgress.messagesScanned && (
              <p className="mt-3 text-sm text-gray-400">
                Estimated time remaining: ~{Math.max(1, Math.ceil(
                  ((scanProgress.messagesFound - scanProgress.messagesScanned) / scanProgress.messagesScanned)
                  * (Date.now() - scanStartTimeRef.current) / 60000
                ))} min
              </p>
            )}
          </div>
        </div>
      )}

      {/* Preview State — Loan Book Wizard */}
      {phase === 'preview' && (
        <>
          {dedupCount > 0 && (
            <div className="mb-4 mx-4 bg-blue-900/30 border border-blue-700/50 rounded-lg px-4 py-3 text-sm text-blue-300">
              {dedupCount} file{dedupCount !== 1 ? 's' : ''} already uploaded — showing {scoutedFiles.length} new file{scoutedFiles.length !== 1 ? 's' : ''}
            </div>
          )}
          {scoutedFiles.length === 0 ? (
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">All files already uploaded!</h3>
              <p className="text-sm text-gray-400 mb-4">
                All {dedupCount} commission files from your email have been uploaded.
              </p>
              <button onClick={handleReset} className="text-sm text-blue-400 hover:text-blue-300">
                Scan Again
              </button>
            </div>
          ) : (
            <LoanBookWizard
              scoutedFiles={scoutedFiles}
              companyId={companyId}
              priorGroupNames={priorGroupNames}
              onImport={handleWizardImport}
              onCancel={handleReset}
            />
          )}
        </>
      )}

      {/* Importing State */}
      {phase === 'importing' && (
        <div className="text-center py-8">
          <div className="mb-4">
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <span className="text-sm font-semibold text-blue-400">
                  Importing files...
                </span>
                <span className="text-sm font-semibold text-blue-400">
                  {importProgress.current} / {importProgress.total}
                </span>
              </div>
              <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-700">
                <div
                  style={{
                    width: `${(importProgress.current / importProgress.total) * 100}%`,
                  }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
                ></div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-400">
            Uploading {UPLOAD_BATCH_SIZE} files at a time...
          </p>
        </div>
      )}

      {/* Complete State */}
      {phase === 'complete' && results && (
        <div className="text-center py-8">
          <div className="mb-4">
            <svg
              className={`mx-auto h-16 w-16 ${results.filesErrored > 0 ? 'text-amber-400' : 'text-green-400'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={results.filesErrored > 0
                  ? "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                }
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {results.filesErrored > 0 ? 'Import Partially Complete' : 'Import Complete!'}
          </h3>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 max-w-sm mx-auto mb-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-left text-gray-400">Files imported:</div>
              <div className="text-right font-medium text-green-400">
                {results.filesImported}
              </div>
              {results.filesDuplicate > 0 && (
                <>
                  <div className="text-left text-gray-400">Already imported:</div>
                  <div className="text-right font-medium text-yellow-400">
                    {results.filesDuplicate}
                  </div>
                </>
              )}
              {results.filesErrored > 0 && (
                <>
                  <div className="text-left text-gray-400">Errors:</div>
                  <div className="text-right font-medium text-red-400">
                    {results.filesErrored}
                  </div>
                </>
              )}
            </div>
          </div>
          {results.errors.length > 0 && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 max-w-md mx-auto mb-4 text-left">
              <p className="text-sm font-medium text-red-300 mb-1">Errors:</p>
              <ul className="text-xs text-red-400 list-disc list-inside">
                {results.errors.slice(0, 5).map((err, i) => (
                  <li key={i} className="truncate">{err}</li>
                ))}
                {results.errors.length > 5 && (
                  <li>...and {results.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
          {results.filesErrored > 0 && failedAssignments.length > 0 && (
            <button
              onClick={() => handleWizardImport(failedAssignments, new Set())}
              className="mt-1 mb-3 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Retry {failedAssignments.length} Failed File{failedAssignments.length !== 1 ? 's' : ''}
            </button>
          )}
          <p className="text-sm text-gray-400 mb-4">
            {stagingMode
              ? "Your files have been submitted for review. We'll notify you once they're set up."
              : 'Your files are now being processed. This may take a few minutes.'}
          </p>
          <button
            onClick={handleReset}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Scan Again
          </button>
        </div>
      )}

      {/* Error State */}
      {phase === 'error' && (
        <div className="text-center py-8">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-red-400 mb-4">{error}</p>
          <button
            onClick={handleReset}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailScoutScanner;
